import { captureException } from '@sentry/react';
import { saveAs } from 'file-saver';
import type { AuthService } from './AuthService';
import { serializeResponseForSentry } from './errorTracking/response.utils';

/**
 * @throws when status not in range [200, 299]
 */
const checkStatus = (response: Response, okStatusCodes?: number[]) => {
  if (response.ok) {
    return response;
  }
  if ((okStatusCodes || []).includes(response.status)) {
    return response;
  }

  captureException(new Error('request https status not ok'), {
    extra: { response: serializeResponseForSentry(response) },
    tags: { failedUrl: response.url, httpStatus: response.status },
    fingerprint: ['http-not-ok'],
  });
  if (response.statusText !== '') {
    const error: Error & { response?: Response } = new Error(response.statusText);
    error.response = response;
    throw error;
  }
  throw response;
};

export class ApiService {
  baseUrl!: string;
  authService!: AuthService;

  init(baseUrl: string, authService: AuthService) {
    this.baseUrl = baseUrl;
    this.authService = authService;
  }

  getToken() {
    return this.authService.getValidToken();
  }

  /**
   * If the endpoint can respond with 404 or 204,
   * remember to pass <undefined | SomeType> as the generic type.
   */
  requestJson<T = any>(options: RequestOptions & { schema?: any }): Promise<T> {
    // Allow pass custom userId to SelfService API on localhost
    const impersonate_id = location.hostname === 'localhost' && localStorage.getItem('impersonate_id');
    if (impersonate_id) {
      options.url += `${options.url.includes('?') ? '&' : '?'}cid=${impersonate_id}`;
    }
    const request = () =>
      this.request(options).then(async (res) => {
        // Specifically handle response codes without body
        // 401 only considered ok if passed in via "okStatusCodes"
        if (res.status === 404 || res.status === 204) {
          return undefined as T;
        }
        try {
          // Clone res so we can read .text() later if this fails
          // Otherwise the body stream is consumed and cant be read again.
          const json = (await res.clone().json()) as T;
          return json;
        } catch (error) {
          captureException(error, {
            extra: { response: serializeResponseForSentry(res) },
            tags: { failedUrl: options.url, httpStatus: res.status, httpBody: (await res.text()).substring(0, 100) },
            fingerprint: ['api-json-parse-error'],
          });
          throw error;
        }
      });
    if (options.method === 'GET') {
      return getFromCacheOrNetwork(['GET', options.url], request);
    }
    return request();
  }
  getJson<T = any>(url: string, schema?: any): Promise<T> {
    return this.requestJson({ url, method: 'GET', schema });
  }
  postJson<T = any>(url: string, payload?: any, schema?: any): Promise<T> {
    return this.requestJson({ url, method: 'POST', schema, payload });
  }

  request({ url, payload, ...rest }: RequestOptions): Promise<Response> {
    const absoluteUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    return this.requestAbsolute({ url: absoluteUrl, payload, ...rest });
  }

  async requestAbsolute({ url, method, payload, okStatusCodes }: RequestOptions): Promise<Response> {
    const token = this.getToken();

    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };
    if (payload) options.body = JSON.stringify(payload);

    const _start = Date.now();
    try {
      const res = await fetch(url, options);
      checkStatus(res, okStatusCodes);
      return res;
    } catch (err) {
      const tags: any = {};
      const extra: any = {};
      if (payload) {
        extra.payload = payload;
      }
      // extra handling for failed price requests:
      if (url.includes('/price') && Array.isArray(payload)) {
        tags.pricePayload = JSON.stringify(payload).substring(0, 100);
      }
      captureException(err, {
        extra: { url: url, ...extra },
        tags: { failedUrl: url, httpDuration: Date.now() - _start, ...tags },
        fingerprint: ['http-exception'],
      });
      throw err;
    }
  }

  requestPdfFile({ url, filename }: { url: string; filename: string }) {
    const token = this.getToken();
    const fetchUrl = `${this.baseUrl}${url}`;
    const options = {
      headers: {
        'Content-Type': 'application/pdf',
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    };

    return fetch(fetchUrl, options)
      .then((response) => response.blob())
      .then((blob) => saveAs(blob, filename));
  }
}

interface RequestOptions {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  payload?: any;
  okStatusCodes?: number[];
}

/**
 * A simple promise cache with a time-to-live (TTL) mechanism.
 * The default TTL is 2 seconds. And serves mostly not to call API's multiple times
 * just because react components are re-rendered.
 */
const getFromCacheOrNetwork = async <T = any>(key: string | string[], fetcher: () => Promise<T>, ttl = 2_000) => {
  const _key = Array.isArray(key) ? key.join(' | ') : key;
  const cached = _promiseCache.get(_key);
  if (cached) {
    if (Date.now() - cached.timestamp < ttl) {
      console.debug('[api] cache hit for', _key);
      return cached.promise;
    }
  }
  const promise = fetcher();
  _promiseCache.set(_key, { promise, timestamp: Date.now() });
  return promise;
};
const _promiseCache: Map<string, { promise: Promise<any>; timestamp: number }> = new Map();
